import React from "react";

import { Box, TextField, Typography } from "@mui/material";

import { FieldSchema } from "../../types";

interface TimestampInputProps {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  touched?: boolean;
}

/** Returns true if a message field looks like a google.protobuf.Timestamp */
export function isTimestampField(field: FieldSchema): boolean {
  if (field.type !== "message" || !field.messageFields) return false;
  const names = field.messageFields.map((f) => f.name);
  return names.includes("seconds") && names.includes("nanos");
}

function toDatetimeLocal(value: unknown): string {
  if (typeof value !== "object" || value === null) return "";
  const { seconds } = value as Record<string, unknown>;
  if (typeof seconds !== "number") return "";
  const date = new Date(seconds * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  // datetime-local requires "YYYY-MM-DDTHH:mm" in the input's local time,
  // not UTC - toISOString() would shift the displayed value for non-UTC users.
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDatetimeLocal(dtLocal: string): Record<string, number> | "" {
  if (!dtLocal) return "";
  const ms = new Date(dtLocal).getTime();
  const seconds = Math.floor(ms / 1000);
  const nanos = (ms % 1000) * 1_000_000;
  return { seconds, nanos };
}

export const TimestampInput: React.FC<TimestampInputProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const converted = fromDatetimeLocal(e.target.value);
    onChange(converted === "" ? undefined : converted);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: "text.primary",
          fontSize: "0.9rem",
          mb: 1,
        }}
      >
        {field.name}
        {!field.isOptional && " *"}
      </Typography>
      {field.description && (
        <Typography
          variant="caption"
          sx={{
            mb: 1,
            display: "block",
            color: "text.secondary",
            fontSize: "0.8rem",
          }}
        >
          {field.description}
        </Typography>
      )}
      <TextField
        type="datetime-local"
        size="small"
        fullWidth
        name={field.name}
        value={toDatetimeLocal(value)}
        onChange={handleChange}
        error={!!error && touched}
        helperText={error && touched ? error : undefined}
        InputLabelProps={{ shrink: true }}
      />
    </Box>
  );
};
